import React, { useRef, useState } from 'react';
import { Microphone, Stop, SpinnerGap, X, ArrowsClockwise, Check } from '@phosphor-icons/react';

export const VoiceExpenseModal = ({ api, onParsed, onClose }) => {
  const [recording, setRecording] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [parsed, setParsed] = useState(null);
  const [error, setError] = useState('');
  const mediaRecorder = useRef(null);
  const chunks = useRef([]);

  const start = async () => {
    try {
      setError('');
      setParsed(null);
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorder.current = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      chunks.current = [];
      mediaRecorder.current.ondataavailable = (e) => { if (e.data.size > 0) chunks.current.push(e.data); };
      mediaRecorder.current.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunks.current, { type: 'audio/webm' });
        setProcessing(true);
        try {
          const fd = new FormData();
          fd.append('audio', blob, 'expense.webm');
          const res = await api.post('/expenses/voice-expense', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
          setParsed(res.data);
        } catch (err) {
          setError(err?.response?.data?.detail || 'Could not process audio');
        }
        setProcessing(false);
      };
      mediaRecorder.current.start();
      setRecording(true);
    } catch (_) {
      setError('Microphone access denied — please allow the mic permission');
    }
  };

  const stop = () => {
    if (mediaRecorder.current?.state === 'recording') mediaRecorder.current.stop();
    setRecording(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="voice-expense-modal">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-card border border-border rounded-2xl p-6 w-full max-w-md">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-heading text-xl font-bold">बोलकर खर्च जोड़ें</h3>
            <p className="text-xs text-muted-foreground mt-0.5">Speak the expense in Hindi or English</p>
          </div>
          <button onClick={onClose} data-testid="voice-expense-close" className="p-1 rounded-lg hover:bg-muted"><X size={20} /></button>
        </div>

        {!parsed && (
          <div className="flex flex-col items-center py-6 gap-4">
            {processing ? (
              <>
                <SpinnerGap size={44} className="animate-spin text-[#FF6B35]" />
                <p className="text-sm text-muted-foreground">समझ रहे हैं… (understanding)</p>
              </>
            ) : (
              <>
                <button onClick={recording ? stop : start} data-testid="voice-expense-record-btn"
                  className={`w-24 h-24 rounded-full flex items-center justify-center transition-all ${recording ? 'bg-red-500 animate-pulse' : 'bg-[#FF6B35] hover:scale-105'}`}>
                  {recording ? <Stop size={40} weight="fill" className="text-white" /> : <Microphone size={40} weight="fill" className="text-white" />}
                </button>
                <p className="text-sm text-muted-foreground text-center">
                  {recording ? 'बोलिए… फिर रुकने के लिए दबाएँ' : 'माइक दबाएँ और बोलें — जैसे:'}
                </p>
                {!recording && (
                  <p className="text-xs text-muted-foreground/80 text-center italic px-4">
                    "आज सब्ज़ी के लिए ढाई सौ रुपये दिए, अपने पैसे से"<br />
                    "Do sau rupaye doodh ke liye, cash"
                  </p>
                )}
              </>
            )}
            {error && <p className="text-sm text-red-500 text-center" data-testid="voice-expense-error">{error}</p>}
          </div>
        )}

        {parsed && (
          <div className="space-y-4" data-testid="voice-expense-result">
            <div className="rounded-xl bg-muted/40 px-4 py-3">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">आपने कहा (You said)</p>
              <p className="text-sm">{parsed.transcript}</p>
            </div>
            {parsed.parse_failed ? (
              <p className="text-sm text-amber-500">समझ नहीं पाए — कृपया फिर से बोलें या हाथ से भरें</p>
            ) : (
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="rounded-xl bg-muted/40 px-3 py-2"><span className="text-muted-foreground text-xs block">Amount</span><span className="font-bold">Rs.{Number(parsed.amount || 0).toLocaleString('en-IN')}</span></div>
                <div className="rounded-xl bg-muted/40 px-3 py-2"><span className="text-muted-foreground text-xs block">Category</span>{parsed.category || '-'}</div>
                <div className="rounded-xl bg-muted/40 px-3 py-2 col-span-2"><span className="text-muted-foreground text-xs block">Description</span>{parsed.description || '-'}</div>
                <div className="rounded-xl bg-muted/40 px-3 py-2"><span className="text-muted-foreground text-xs block">Paid from</span>{parsed.paid_from === 'own_pocket' ? 'अपने पैसे (Own)' : parsed.paid_from === 'advance' ? 'Advance' : 'House cash'}</div>
                <div className="rounded-xl bg-muted/40 px-3 py-2"><span className="text-muted-foreground text-xs block">Method</span>{parsed.payment_method || 'cash'}</div>
              </div>
            )}
            <div className="flex gap-3">
              <button onClick={() => { setParsed(null); setError(''); }} data-testid="voice-expense-retry"
                className="flex-1 py-3 rounded-full border border-border text-sm font-medium hover:bg-muted flex items-center justify-center gap-2">
                <ArrowsClockwise size={16} /> फिर से बोलें
              </button>
              {!parsed.parse_failed && (
                <button onClick={() => onParsed(parsed)} data-testid="voice-expense-use"
                  className="flex-1 py-3 rounded-full bg-[#FF6B35] text-white text-sm font-semibold btn-glow flex items-center justify-center gap-2">
                  <Check size={16} /> ठीक है, भरें
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
